/**
 * LoadProgressCard — floating overlay that narrates loading a drafted
 * document into Word (prepare → download → rebuild → restore → spine).
 *
 * Both "Load doc" entry points run the same multi-second work: fetch/rebuild
 * the run's .docx, encode it, ``insertFileFromBase64`` into Word, then hydrate
 * the spine. Before this card the writer saw only a dimmed button or a frozen
 * "Loading…" label with no sense of progress. This wraps the shared
 * ``HomeLoadProgress`` stepper in a small fixed-position card anchored top-right
 * — the same placement as ``SaveProgressCard`` — so the wait is legible.
 *
 * Two consumers share it:
 *   - the spine's "Load doc" button (``VariantCShell.handleLoadDraftedDocument``)
 *   - the Drafts panel's per-row "Load" button (``DraftsPanel.RunRow.handleLoad``)
 *
 * Pure presentation — each parent owns the step model (``buildLoadSteps``) and
 * advances it across its own load phases.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { HomeLoadProgress, type LoadStep } from "./HomeLoadProgress";

interface LoadProgressCardProps {
  /** Ordered load steps with their current state (from ``buildLoadSteps``). */
  steps: LoadStep[];
  /** Title of the document being loaded — echoed so the writer knows which
   *  run they clicked. Omit to hide the title line. */
  docTitle?: string;
  /** Non-null when the load failed — renders the error frame + dismiss. */
  error?: string | null;
  /** Terminal success frame ("Loaded ✓"), shown briefly before auto-dismiss. */
  done?: boolean;
  /** In-flight header (default "Loading document"). Custom import overrides it. */
  header?: string;
  /** Error header (default "Couldn't load document"). */
  errorHeader?: string;
  /** Success header shown when ``done`` (default "Loaded"). */
  doneHeader?: string;
  /** Dismiss the card (error path — writer acks and returns to editing). */
  onDismiss?: () => void;
}

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    top: "10px",
    right: "10px",
    zIndex: 1000,
    width: "260px",
    maxWidth: "calc(100vw - 20px)",
    // The card itself carries the panel styling; this just positions it.
    filter: "drop-shadow(0 4px 12px rgba(76,51,138,0.14))",
  },
});

export function LoadProgressCard({
  steps,
  docTitle,
  error,
  done,
  header = "Loading document",
  errorHeader = "Couldn't load document",
  doneHeader = "Loaded",
  onDismiss,
}: LoadProgressCardProps): React.ReactElement {
  const styles = useStyles();
  return (
    <div className={styles.overlay}>
      <HomeLoadProgress
        steps={steps}
        docTitle={docTitle}
        error={error}
        done={done}
        header={header}
        errorHeader={errorHeader}
        doneHeader={doneHeader}
        dismissLabel="Dismiss"
        onDismiss={onDismiss}
      />
    </div>
  );
}
